import type { ReactNode } from 'react'

import { useLocation } from 'react-router-dom'
import { styled } from 'styled-components'

import Clickable from 'core/components/lib/Clickable'
import { usePeachIdLink, usePeachIdTable } from 'core/helpers/peachIdLink'

type PeachIdLinkProps = {
  /** The raw value. Anything that is not a Peach id is passed through. */
  value: unknown
  /** The field the value sits in, used to identify the resource. */
  fieldName?: string
  /** The record the value came from, read for ancestor ids. */
  record: { [key: string]: unknown }
  /** The already-formatted value to display. */
  children: ReactNode
}

/**
 * Wraps a formatted value in a link when it is a Peach id whose destination
 * resolves. Every other case renders `children` untouched, so this is safe to
 * wrap around any value.
 *
 * With no provider there is no router to read, so it returns `children` before
 * any router hook runs.
 */
const PeachIdLink = ({
  value,
  fieldName,
  record,
  children,
}: PeachIdLinkProps) => {
  const table = usePeachIdTable()
  if (!table) return <>{children}</>

  return (
    <ResolvedPeachIdLink fieldName={fieldName} record={record} value={value}>
      {children}
    </ResolvedPeachIdLink>
  )
}

const ResolvedPeachIdLink = ({
  value,
  fieldName,
  record,
  children,
}: PeachIdLinkProps) => {
  const to = usePeachIdLink(value, { fieldName, record })
  const { pathname } = useLocation()

  // Nothing to link, or it links to the page already being viewed.
  if (!to || to === pathname) return <>{children}</>

  return (
    <InlineClickable to={to} variant='link'>
      {children}
    </InlineClickable>
  )
}

export default PeachIdLink

// Clickable's link variant is inline-flex; keep it on the row's text baseline.
// The wrapped ViewModeText sets its own color, so force it to take the link's.
const InlineClickable = styled(Clickable)`
  display: inline;
  text-align: left;

  && * {
    color: inherit;
  }
`
